import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Dialog a11y: on open, move focus into the dialog and remember what had focus;
 * trap Tab within it; close on Escape; restore focus on close. Returns a ref for
 * the dialog element. Depends only on `open` so re-renders don't steal focus.
 *
 * Shared by Schedule, PHM, ConfirmModal, and other suite overlays.
 */
export function useDialog(open, onClose) {
  const ref = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const node = ref.current;
    const prevFocus = document.activeElement;
    const focusables = () =>
      node ? [...node.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null) : [];
    const first = focusables()[0];
    if (first) first.focus();
    else if (node) {
      node.setAttribute('tabindex', '-1');
      node.focus();
    }

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && node) {
        const f = focusables();
        if (!f.length) return;
        const firstEl = f[0];
        const lastEl = f[f.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    node?.addEventListener('keydown', onKey);
    return () => {
      node?.removeEventListener('keydown', onKey);
      if (prevFocus && typeof prevFocus.focus === 'function') prevFocus.focus();
    };
  }, [open]);

  return ref;
}

export default useDialog;
